import React, { useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ModalsContext } from 'r/contexts';
import { unionContexts, insertStyle, getUniqueNumber } from 'r/utils';
import { zModalsWr } from 'r/constants/zIdxs';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { X } from 'lucide-react';
import { useAtomValue } from 'jotai';
import { wiwAtom } from 'r/atoms';

export default unionContexts(Modal, [
  ModalsContext,
  'modals',
  'pushModal',
  'removeModal',
]);

function Modal({
  closeModal,
  children,
  modals,
  pushModal,
  removeModal,
  fullScreen,
}: {
  closeModal?: () => void;
  children: React.ReactElement;
  modals: Array<{ id: number; rootScrollTop: number }>;
  pushModal: (modal: { rootScrollTop: number; id: number }) => void;
  removeModal: (id: number) => void;
  fullScreen?: boolean;
}) {
  const wiw = useAtomValue(wiwAtom);
  const isFullScreen =
    typeof fullScreen === 'boolean' ? fullScreen : wiw <= 767;

  const modalsRef = useRef(modals);
  useLayoutEffect(() => {
    modalsRef.current = modals;
  }, [modals]);

  const idRef = useRef(getUniqueNumber());
  const wrRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const [lastModal] = modals.slice(-1);
    const rootScrollTop = lastModal ? lastModal.rootScrollTop : window.scrollY;
    pushModal({ rootScrollTop, id: idRef.current });
    const needScrollbar =
      lastModal ?
        (lastModal as { needScrollbar?: boolean }).needScrollbar
      : document.body.scrollHeight > window.innerHeight;

    const cleanStyle = insertStyle(
      isFullScreen ?
        `
          body {
            position: absolute;
            height: 100%;
            width: 100%;
          }
        `
      : `
      ${needScrollbar ? 'body {overflow-y: scroll;}' : ''}
      #root {
        position: fixed;
        top: -${rootScrollTop}px;
      }
    `,
    );

    if (isFullScreen && wrRef.current) {
      disableBodyScroll(wrRef.current, {
        allowTouchMove: (target: HTMLElement) =>
          wrRef.current?.contains(target) ?? false,
      });
    }
    window.scrollTo(0, 0);

    return () => {
      removeModal(idRef.current);
      if (isFullScreen && wrRef.current) {
        enableBodyScroll(wrRef.current);
      }
      setTimeout(() => {
        cleanStyle();
        if (modalsRef.current.slice(-1)[0]?.id === idRef.current) {
          window.scrollTo(
            0,
            modalsRef.current.length === 1 ? rootScrollTop : 0,
          );
        }
      }, 0);
    };
  }, [isFullScreen, pushModal, removeModal, modals]);

  const isOverlayPressedRef = useRef(false);
  const isHidden = modals.slice(-1)[0]?.id !== idRef.current;

  return createPortal(
    <div
      ref={wrRef}
      className={`absolute left-0 top-0 right-0 min-h-full w-full items-center justify-center overflow-x-hidden ${
        isHidden ? 'hidden' : 'flex'
      }`}
      style={{ zIndex: zModalsWr }}
      onMouseDown={(event) => {
        event.stopPropagation();
        if (event.target === event.currentTarget) {
          isOverlayPressedRef.current = true;
        }
      }}
      onMouseUp={(event) => {
        if (
          event.target === event.currentTarget &&
          isOverlayPressedRef.current
        ) {
          closeModal?.();
        }
        isOverlayPressedRef.current = false;
      }}
    >
      <div className={`absolute inset-0 bg-background opacity-70`} />
      <div
        className={`relative z-10 flex flex-col overflow-hidden bg-background shadow-lg ${
          isFullScreen ?
            'fixed inset-0 overflow-auto rounded-none shadow-none'
          : 'rounded-3xl max-w-full'
        }`}
      >
        {closeModal && (
          <button
            onClick={() => closeModal()}
            className={`absolute z-10 text-muted-foreground hover:text-foreground transition-colors ${
              isFullScreen ?
                'right-9 top-10 max-[375px]:right-8 max-[375px]:top-8'
              : 'right-4 top-4'
            }`}
            aria-label="Close"
          >
            <X className={isFullScreen ? 'w-6 h-6' : 'w-8 h-8'} />
          </button>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}
